import { useState, useEffect, useCallback } from 'react';
import { syncManager, SyncStatus } from '@/lib/sync-manager';
import { toast } from 'sonner';

export function useBackgroundSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [drafts, setDrafts] = useState<any[]>([]);

  useEffect(() => {
    // Initialize sync manager
    syncManager.init();

    // Update pending count
    const updateCount = async () => {
      const count = await syncManager.getPendingCount();
      setPendingCount(count);
    };

    updateCount();

    // Subscribe to sync status updates
    const unsubscribe = syncManager.subscribe((status: SyncStatus) => {
      if (status.count !== undefined) {
        setPendingCount(status.count);
      }

      setIsSyncing(status.type === 'sync-started');

      // Show toast notifications
      if (status.type === 'sync-completed') {
        toast.success(status.message);
      } else if (status.type === 'sync-error') {
        toast.error(status.message);
      } else if (status.type === 'sync-partial') {
        toast.warning(status.message);
      } else if (status.type === 'draft-saved') {
        toast.info(status.message, { duration: 2000 });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const saveDraft = useCallback(async (type: string, id: string, data: any) => {
    await syncManager.saveDraft(type, id, data);
  }, []);

  const getDraft = useCallback(async (type: string, id: string) => {
    return await syncManager.getDraft(type, id);
  }, []);

  const deleteDraft = useCallback(async (type: string, id: string) => {
    await syncManager.deleteDraft(type, id);
  }, []);

  const getAllDrafts = useCallback(async () => {
    const allDrafts = await syncManager.getAllDrafts();
    setDrafts(allDrafts);
    return allDrafts;
  }, []);

  const queueSync = useCallback(async (
    type: 'case' | 'patient' | 'task' | 'inventory' | 'call_log' | 'work_log',
    action: 'create' | 'update' | 'delete',
    data: any,
    itemId?: string
  ) => {
    return await syncManager.queueSync(type, action, data, itemId);
  }, []);

  const syncAll = useCallback(async () => {
    await syncManager.syncAll();
  }, []);

  const retryFailed = useCallback(async () => {
    await syncManager.retryFailedSyncs();
  }, []);

  return {
    pendingCount,
    isSyncing,
    drafts,
    saveDraft,
    getDraft,
    deleteDraft,
    getAllDrafts,
    queueSync,
    syncAll,
    retryFailed,
  };
}
